<template>
  <div>
    <v-alert v-show="failed.length > 0" dense prominent outlined type="error">
      <v-card class="mx-auto alert-cart" outlined>
        <v-card-text>
          <div class="text-overline mb-4">
            {{ failed.length }} Methode{{
              failed.length > 1 ? 'n' : ''
            }}
            hatte{{ failed.length > 1 ? 'n' : '' }}
            einen Fehler in der letzten Ausführung
          </div>
          <p v-for="f in failed" :key="`${f.id}.alert-text`">
            {{ f.method }}: {{ formatDateTime(f.lastExecution) }}
            <a @click="showLog(f, true)"> Log anzeigen </a>
          </p>
        </v-card-text>
      </v-card>
    </v-alert>
    <v-alert v-show="running.length > 0" dense prominent outlined type="info">
      <v-card class="mx-auto alert-cart" outlined>
        <v-card-text>
          <div class="text-overline mb-4">
            {{ running.length }} Methode{{ running.length > 1 ? 'n' : '' }}
            {{ running.length > 1 ? 'werden' : 'wird' }} aktuell ausgeführt
          </div>
          <div v-for="r in running" :key="`${r.id}.running-text`">
            <p>
              {{ r.method }}: {{ formatDateTime(r.running.createdAt) }}
              <a @click="showLog(r, true)"> Log anzeigen </a> <br />
              <span>Erwartetes Ende: {{ getEstimatedEnd(r) }}</span>
            </p>
          </div>
        </v-card-text>
      </v-card>
    </v-alert>
    <div>
      <v-data-table
        :items="methods"
        :headers="tableHeaders"
        :search="tableSearch"
        :group-by="groupTableByTag ? 'tag' : null"
        :expanded.sync="tableExpanded"
        show-expand
        single-expand
      >
        <template v-slot:top>
          <v-toolbar flat>
            <div
              class="pa-2 d-flex justify-space-between align-center"
              style="width: 100%"
            >
              <v-switch
                v-model="groupTableByTag"
                class="mt-6 mr-3"
                style="display: flex; justify-content: center"
                label="Gruppieren"
              ></v-switch>
              <v-switch
                v-model="relativeDateTimes"
                class="mt-6"
                style="display: flex; justify-content: center"
                label="Relative Zeiten"
              ></v-switch>
              <v-text-field
                v-model="tableSearch"
                placeholder="Suche"
                class="mt-5 px-9"
              />
            </div>
          </v-toolbar>
        </template>
        <template v-slot:[`item.method`]="{ item }">
          <v-list-item-content>
            <v-list-item-title>
              <b>{{ item.method }}</b>
            </v-list-item-title>
            <v-list-item-subtitle>
              {{ item.package.replace('de.goetel.spot', '..') }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </template>
        <template v-slot:[`item.schedule`]="{ item }">
          {{ createCronString(item) }}
        </template>
        <template v-slot:[`item.lastExecution`]="{ item }">
          {{ formatDateTime(item.lastExecution) }}
        </template>
        <template v-slot:[`item.nextExecution`]="{ item }">
          {{ formatDateTime(item.nextExecution) }}
        </template>
        <template v-slot:[`item.lastExecutionFailed`]="{ item }">
          <span :style="{ color: item.lastExecutionFailed ? 'red' : 'lime' }">{{
            item.lastExecutionFailed ? 'Ja' : 'Nein'
          }}</span>
        </template>
        <template v-slot:[`item.statistic.minDuration`]="{ item }">
          <template v-if="item.statistic">
            <span
              v-show="item.statistic.minDuration"
              v-html="
                formatDuration(
                  Math.round(item.statistic.minDuration * 10) / 10,
                  true
                )
              "
            ></span>
          </template>
        </template>
        <template v-slot:[`item.statistic.maxDuration`]="{ item }">
          <template v-if="item.statistic">
            <span
              v-show="item.statistic.minDuration"
              v-html="
                formatDuration(
                  Math.round(item.statistic.maxDuration * 10) / 10,
                  true
                )
              "
            ></span>
          </template>
        </template>
        <template v-slot:[`item.statistic.averageDuration`]="{ item }">
          <template v-if="item.statistic">
            <span
              v-show="item.statistic.averageDuration"
              v-html="
                formatDuration(
                  Math.round(item.statistic.averageDuration * 10) / 10,
                  true
                )
              "
            ></span>
          </template>
        </template>
        <template v-slot:[`item.statistic.medianDuration`]="{ item }">
          <template v-if="item.statistic">
            <span
              v-show="item.statistic.medianDuration"
              v-html="
                formatDuration(
                  Math.round(item.statistic.medianDuration * 10) / 10,
                  true
                )
              "
            ></span>
          </template>
        </template>
        <template v-slot:[`item.statistic.failRate`]="{ item }">
          <template v-if="item.statistic && item.statistic.failRate > 0">
            <span
              :title="`${item.statistic.failedCount} von ${item.statistic.totalCount}`"
            >
              {{ item.statistic.failRate * 100 }}%
            </span>
          </template>
        </template>
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="py-4">
            <h4>Log Einträge {{ item.method }}</h4>
            <template v-if="logEntries.length === 0">
              <v-progress-circular
                indeterminate
                color="primary"
              ></v-progress-circular>
            </template>
            <template v-else>
              <v-data-table :items="logEntries" :headers="logTableHeaders">
                <template v-slot:[`item.createdAt`]="{ item: log }">
                  {{ formatDateTime(log.createdAt) }}
                </template>
                <template v-slot:[`item.finishedAt`]="{ item: log }">
                  {{ formatDateTime(log.finishedAt) }}
                </template>
                <template v-slot:[`item.abortedAt`]="{ item: log }">
                  {{ formatDateTime(log.abortedAt) }}
                </template>
                <template v-slot:[`item.errorMessage`]="{ item: log }">
                  <a @click="showErrorStacktrace(log)">{{
                    log.errorMessage
                  }}</a>
                </template>
              </v-data-table>
            </template>
          </td>
        </template>
      </v-data-table>
    </div>
    <v-dialog v-model="chartDialog" fullscreen hide-overlay>
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          class="my-4 float-end"
          color="primary"
          dark
          v-bind="attrs"
          v-on="on"
        >
          Chart Dialog anzeigen
        </v-btn>
      </template>
      <v-card>
        <v-toolbar color="secondary">
          <v-btn icon dark @click="chartDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>Execution Chart</v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>
        <div v-if="showChart" class="mt-4 mx-4">
          <v-form>
            <v-container>
              <v-row>
                <v-col cols="12" sm="2">
                  <v-text-field
                    v-model="startDate"
                    label="Start Datum"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="2">
                  <v-text-field
                    v-model="startTime"
                    label="Start Uhrzeit"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="2">
                  <v-text-field
                    v-model="endDate"
                    label="End Datum"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="2">
                  <v-text-field
                    v-model="endTime"
                    label="End Uhrzeit"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="2">
                  <v-btn @click="generateDates"> Anzeigen </v-btn>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
          <div id="chart">
            <apexchart
              v-if="series.length > 0"
              type="rangeBar"
              :height="chartHeight"
              :options="chartOptions"
              :series="series"
              theme="dark"
            ></apexchart>
          </div>
        </div>
        <div v-else>
          <v-btn @click="showChart = true">Chart laden</v-btn>
        </div>
      </v-card>
    </v-dialog>
    <v-dialog v-model="stacktraceDialog" scrollable>
      <v-card v-if="stacktraceDialog">
        <v-card-title class="text-h5">
          <code>{{ errorLog.errorMessage }}</code>
          &nbsp;Stacktrace
        </v-card-title>

        <v-card-text style="height: 80vh" class="custom-scrollbar">
          <pre>{{ errorLog.stacktrace }}</pre>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="
              stacktraceDialog = false;
              errorLog = null;
            "
          >
            Schließen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { HTTP } from '@/main/httpClient';
import * as CronParser from 'cron-parser';
import TimeUtility from '@/util/TimeUtility.js';

const dateTimeFormat = Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  day: 'numeric',
  month: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric'
});

const dateSortAB = function (a, b) {
  return new Date(a) - new Date(b);
};

const dateSortBA = function (a, b) {
  return new Date(b) - new Date(a);
};

export default {
  name: 'SchedulerOverview',
  data: () => ({
    loading: true,
    time: null,
    showChart: false,
    startDate: new Date().toISOString().substring(0, 10),
    startTime: '00:00:00',
    endDate: new Date().toISOString().substring(0, 10),
    endTime: '23:59:59',
    startDateTime: null,
    endDateTime: null,
    tPicker: false,
    allMethods: [],
    typeToggle: 2, // 0: All, 1: Methods, 2: Scheduler
    statistics: [],
    statisticDays: 7,
    chartDialog: false,
    chartOptions: {
      chart: {
        animations: {
          enabled: false
        }
      },
      tooltip: {},
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: '80%'
        }
      },
      xaxis: {
        type: 'datetime',
        labels: {
          datetimeUTC: false
        }
      },
      stroke: {
        width: 1
      },
      fill: {
        type: 'solid',
        opacity: 0.6
      },
      legend: {
        horizontalAlign: 'left'
      },
      theme: {
        palette: 'palette1'
      }
    },
    groupTableByTag: false,
    relativeDateTimes: true,
    tableSearch: '',
    tableExpanded: [],
    logEntries: [],
    stacktraceDialog: false,
    errorLog: null,
    tableHeaders: [
      {
        text: 'Scheduler',
        value: 'method'
      },
      {
        text: 'Cron String',
        value: 'schedule'
      },
      {
        text: 'Letzte Ausführung',
        value: 'lastExecution',
        sort: dateSortBA
      },
      {
        text: 'Nächste Ausführung',
        value: 'nextExecution',
        sort: dateSortAB
      },
      {
        text: 'Letzte Ausführung Failed',
        value: 'lastExecutionFailed'
      },
      {
        text: 'Min',
        value: 'statistic.minDuration'
      },
      {
        text: 'Max',
        value: 'statistic.maxDuration'
      },
      {
        text: 'Average',
        value: 'statistic.averageDuration'
      },
      {
        text: 'Median',
        value: 'statistic.medianDuration'
      },
      {
        text: 'Fehlerquote',
        value: 'statistic.failRate'
      }
    ],
    logTableHeaders: [
      {
        text: 'Start',
        value: 'createdAt'
      },
      {
        text: 'Ende',
        value: 'finishedAt'
      },
      {
        text: 'Ausführung abgebrochen',
        value: 'abortedAt'
      },
      {
        text: 'Error',
        value: 'errorMessage'
      }
    ]
  }),
  computed: {
    series: function () {
      if (!this.methods || this.methods.length === 0) return [];
      if (this.executions === null) return [];

      const series = this.methods
        .map((m) => {
          const statistic = this.statistics.find((s) => s.id === m.id);
          if (!statistic) return null;

          const seriesName = this.getSeriesName(m);
          const seriesData = this.createSeriesData(m, statistic);

          return {
            name: seriesName + ' (' + seriesData.length + ')',
            data: seriesData,
            hidden: seriesData.length > 50
          };
        })
        .filter((x) => x);

      return series;
    },
    chartHeight: function () {
      return this.series.length * 16 + 200;
    },
    executions: function () {
      if (!this.methods || this.methods.length === 0) return null;
      if (!this.startDateTime || !this.endDateTime) return null;

      return this.methods.reduce((prev, cur) => {
        prev[cur.id] = this.createScheduledJobsForDay(
          this.startDateTime,
          this.endDateTime,
          cur
        );
        return prev;
      }, {});
    },
    methods: function () {
      if (!this.allMethods) return [];
      return this.allMethods.filter(
        (m) => this.typeToggle === 0 || m.executionType === this.typeToggle
      );
    },
    running: function () {
      if (!this.methods) return [];
      return this.methods.filter((m) => m.running);
    },
    failed: function () {
      if (!this.methods) return [];
      return this.methods.filter((m) => m.lastExecutionFailed);
    }
  },
  watch: {
    tableExpanded: function (newVal, oldVal) {
      this.logEntries = [];
      if (!newVal || newVal.length < 1) return;
      this.loadLogEntries(newVal[0].id).then((log) => {
        this.logEntries = log;
      });
    }
  },
  async mounted() {
    this.loading = true;
    this.chartOptions.tooltip.custom = this.createTooltipText;
    this.generateDates();
    Promise.all(this.loadData()).then(([methods, statistics, running]) => {
      this.statistics = statistics;
      this.allMethods = methods.map((m) => {
        m.statistic = statistics.find((s) => s.id === m.id);
        m.running = running.find((r) => r.executionMethodId === m.id);
        return m;
      });

      this.loading = false;
    });
  },
  methods: {
    loadData: function () {
      return [
        this.loadScheduleData(),
        this.loadStatistics(),
        this.loadRunning()
      ];
    },
    loadScheduleData: function () {
      return HTTP.get('execution-logging/method').then(({ data, status }) => {
        if (status !== 200) {
          throw new Error('Fehler beim laden der Daten: ' + status);
        }
        return data.map((s) => {
          const methodParts = s.methodName.split('.');
          s.method = methodParts.pop();
          s.package = methodParts.join('.');
          return s;
        });
      });
    },
    loadStatistics: function () {
      return HTTP.get(
        'execution-logging/log/statistic?days=' + this.statisticDays
      ).then(({ data, status }) => {
        if (status !== 200) {
          throw new Error('Fehler beim laden der Statistik Daten: ' + status);
        }
        return data;
      });
    },
    loadRunning: function () {
      return HTTP.get('execution-logging/log/running').then(
        ({ data, status }) => {
          if (status !== 200) {
            throw new Error('Fehler beim laden der Statistik Daten: ' + status);
          }
          return data;
        }
      );
    },
    loadLogEntries: function (methodId) {
      return HTTP.get('execution-logging/log/' + methodId).then(
        ({ data, status }) => {
          if (status !== 200) {
            throw new Error('Fehler beim laden der Log Einträge: ' + status);
          }
          return data;
        }
      );
    },
    createScheduledJobsForDay: function (startDate, endDate, method) {
      const options = {
        currentDate: startDate,
        endDate,
        iterator: true
      };

      const fixedValues = [
        'tag',
        'description',
        'package',
        'method',
        'id',
        'methodName',
        'timezone'
      ];
      for (const k of Object.keys(method)) {
        if (fixedValues.includes(k) || typeof method[k] !== 'string') continue;
        method[k] = method[k].replace(/\s+/g, ''); // fix for parsing errors like '1, 5' as dayOfWeek value
      }

      let interval;
      try {
        interval = CronParser.parseExpression(
          this.createCronString(method),
          options
        );
      } catch (ex) {
        console.log('Error while parsing method', JSON.stringify(method));
        throw ex;
      }

      const executions = [];
      while (true) {
        try {
          const obj = interval.next();
          executions.push(obj.value.toDate());
        } catch (e) {
          break;
        }
      }

      return executions;
    },
    generateDates: function () {
      if (!this.startDate || !this.startTime) return null;
      if (!this.endDate || !this.endTime) return null;

      let startDate;
      try {
        startDate = new Date(this.startDate);
        const t = this.startTime.split(':');
        startDate.setHours(parseInt(t[0]), parseInt(t[1]), parseInt(t[2]));
      } catch {
        return null;
      }

      let endDate;
      try {
        endDate = new Date(this.endDate);
        const t = this.endTime.split(':');
        endDate.setHours(parseInt(t[0]), parseInt(t[1]), parseInt(t[2]));
      } catch {
        return null;
      }

      if (startDate >= endDate) return null;

      this.startDateTime = startDate;
      this.endDateTime = endDate;
    },
    getSeriesName: function (method) {
      let name = '';
      if (method.tag) name = `[${method.tag}] `;
      name += method.method.split('(')[0];
      return name;
    },
    createSeriesData: function (method, statistic) {
      return this.executions[method.id].map((e) => {
        return {
          x: ' ',
          y: [
            e.getTime(),
            e.setMilliseconds(e.getMilliseconds() + statistic.medianDuration)
          ]
        };
      });
    },
    createTooltipText: function ({ y1, y2, seriesIndex, w, ...rest }) {
      const date = new Date(y1);
      return (
        '<div class="apexcharts-tooltip-rangebar">' +
        `<div><strong style="color: ${w.globals.colors[seriesIndex]}">` +
        w.globals.seriesGroups[0][seriesIndex] +
        '</strong></div><div><span>' +
        this.formatDateTime(date) +
        ' | median duration ' +
        this.formatDuration(y2 - y1) +
        '</span></div></div>'
      );
    },
    formatDateTime: function (dateTime) {
      if (!dateTime) return;
      if (this.relativeDateTimes) return this.formatRelativeDateTime(dateTime);
      return TimeUtility.intlFormatDateTime(dateTime, dateTimeFormat);
    },
    formatDuration: function (duration, alternative = false) {
      const ms = Math.floor(duration % 1000);
      const seconds = Math.floor((duration / 1000) % 60);
      const minutes = Math.floor((duration / (1000 * 60)) % 60);
      const hours = Math.floor((duration / (1000 * 60 * 60)) % 24);

      if (alternative === true) {
        let ret = '';
        if (duration < 1) return '0ms';
        if (hours > 0) ret += hours + 'h ';
        if (minutes > 0) ret += minutes + 'm ';
        if (seconds > 0) ret += seconds + 's ';
        if (ms > 0) ret += ms + 'ms ';
        return ret;
      }

      return `${this.padStart(hours)}:${this.padStart(minutes)}:${this.padStart(
        seconds
      )}.<small>${this.padStart(ms, 3)}</small>`;
    },
    formatRelativeDateTime: function (dateTime) {
      if (!dateTime) return;
      return TimeUtility.intlRelativeTimeFormat(new Date(), dateTime);
    },
    padStart: function (num, n = 2, c = '0') {
      return num.toString().padStart(n, c);
    },
    createCronString: function (method) {
      method.dayOfMonth = method.dayOfMonth.replace('Last', '31');
      return `${method.second} ${method.minute} ${method.hour} ${method.dayOfMonth} ${method.month} ${method.dayOfWeek}`;
    },
    showLog: function (method, filterTable) {
      if (!method) return;
      if (filterTable) {
        this.tableSearch = method.method;
      }
      this.tableExpanded = [method];
    },
    showErrorStacktrace: function (log) {
      console.log(log);
      this.errorLog = log;
      this.stacktraceDialog = true;
    },
    getEstimatedEnd: function (method) {
      if (!method || !method.running || !method.statistic) {
        return 'Erwartetes Ende kann nicht ermittelt werden';
      }

      const { running, statistic } = method;
      if (statistic.medianDuration === null) {
        return 'Erwartetes Ende kann nicht ermittelt werden';
      }

      const expectedEnd = new Date(
        Date.parse(running.createdAt) + statistic.medianDuration
      );

      return this.formatDateTime(expectedEnd);
    }
  }
};
</script>

<style>
.alert-cart {
  background-color: unset !important;
  border: unset !important;
}

.apexcharts-tooltip {
  background-color: var(--goe-background-light) !important;
  color: var(--goe-foreground-light) !important;
}

.apexcharts-text {
  fill: var(--goe-fontColor-dark) !important;
}

.apexcharts-legend-text {
  color: var(--goe-fontColor-light) !important;
}

.apexcharts-menu {
  background-color: var(--goe-background-highlight) !important;
}

.apexcharts-menu-item:hover {
  background-color: var(--goe-background-light) !important;
}
</style>
